import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor to add auth token and company header
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const selectedCompanyId = localStorage.getItem('selectedCompanyId');
  if (selectedCompanyId) {
    config.headers['X-Company-Id'] = selectedCompanyId;
  }
  return config;
});

// Response interceptor for auth errors and ID normalization
const addIdAlias = (obj) => {
  if (!obj || typeof obj !== 'object' || obj instanceof Date) return obj;
  if (Array.isArray(obj)) { obj.forEach(addIdAlias); return obj; }
  if (obj.id !== undefined && obj._id === undefined) obj._id = obj.id;
  for (const k in obj) { if (Object.prototype.hasOwnProperty.call(obj, k)) { const v = obj[k]; if (v && typeof v === 'object') addIdAlias(v); } }
  return obj;
};

api.interceptors.response.use(
  (response) => {
    if (response.data) {
      if (Array.isArray(response.data)) {
        response.data.forEach(addIdAlias);
      } else if (typeof response.data === 'object') {
        addIdAlias(response.data);
        if (Array.isArray(response.data.data)) response.data.data.forEach(addIdAlias);
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/verify-otp');
      if (!isLoginRequest) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Store message so login page can display it
        const msg = error.response?.data?.message || '';
        if (msg.toLowerCase().includes('session expired')) {
          localStorage.setItem('sessionExpiredMsg', 'Your session has expired. Please log in again.');
        }
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  requestAccess: (data) => api.post('/auth/request-access', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  verifyOtp: (data) => api.post('/auth/verify-otp', data),
  resendOtp: (data) => api.post('/auth/resend-otp', data),
};

// Users
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  updatePermissions: (id, data) => api.put(`/users/${id}/permissions`, data),
  delete: (id) => api.delete(`/users/${id}`),
  // Super admin endpoints
  getSuperAdmins: () => api.get('/users/super-admins'),
  createSuperAdmin: (data) => api.post('/users/super-admin', data),
  getAllCompanies: () => api.get('/users/all-companies'),
  getCompanyUsers: (companyId) => api.get(`/users/company/${companyId}/users`),
};

// Companies
export const companiesAPI = {
  getAll: () => api.get('/companies'),
  getCurrent: () => api.get('/companies/current'),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  updateSettings: (id, data) => api.put(`/companies/${id}/settings`, data),
  uploadLogo: (id, file) => {
    const fd = new FormData();
    fd.append('logo', file);
    return api.post(`/companies/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogo: (id) => api.delete(`/companies/${id}/logo`),
};

// Contractors
export const contractorsAPI = {
  getAll: (params) => api.get('/contractors', { params }),
  getById: (id) => api.get(`/contractors/${id}`),
  create: (data) => api.post('/contractors', data),
  update: (id, data) => api.put(`/contractors/${id}`, data),
  delete: (id) => api.delete(`/contractors/${id}`),
};

// Providers
export const providersAPI = {
  getAll: (params) => api.get('/providers', { params }),
  getById: (id) => api.get(`/providers/${id}`),
  create: (data) => api.post('/providers', data),
  update: (id, data) => api.put(`/providers/${id}`, data),
  delete: (id) => api.delete(`/providers/${id}`),
  addService: (id, data) => api.post(`/providers/${id}/services`, data),
  removeService: (id, serviceId) => api.delete(`/providers/${id}/services/${serviceId}`),
  addContractor: (id, data) => api.post(`/providers/${id}/contractors`, data),
  updateContractor: (id, assignmentId, data) => api.put(`/providers/${id}/contractors/${assignmentId}`, data),
  removeContractor: (id, assignmentId) => api.delete(`/providers/${id}/contractors/${assignmentId}`),
};

// Provider Services
export const providerServicesAPI = {
  getAll: (params) => api.get('/provider-services', { params }),
  create: (data) => api.post('/provider-services', data),
  update: (id, data) => api.put(`/provider-services/${id}`, data),
  delete: (id) => api.delete(`/provider-services/${id}`),
};

// Assignments
export const assignmentsAPI = {
  getAll: () => api.get('/assignments'),
  create: (data) => api.post('/assignments', data),
  update: (id, data) => api.put(`/assignments/${id}`, data),
  delete: (id) => api.delete(`/assignments/${id}`),
};

// Collections
export const collectionsAPI = {
  getAll: (params) => api.get('/collections', { params }),
  getById: (id) => api.get(`/collections/${id}`),
  create: (data) => api.post('/collections', data),
  update: (id, data) => api.put(`/collections/${id}`, data),
  delete: (id, params) => api.delete(`/collections/${id}`, { params }),
  getProviderServices: (providerId) => api.get(`/collections/provider/${providerId}/services`),
  generateInvoices: (id) => api.post(`/collections/${id}/generate-invoices`),
};

// Provider Invoices
export const providerInvoicesAPI = {
  getAll: (params) => api.get('/provider-invoices', { params }),
  getById: (id) => api.get(`/provider-invoices/${id}`),
  generate: (data) => api.post('/provider-invoices/generate', data),
  preview: (data) => api.post('/provider-invoices/preview', data),
  bulkGenerate: (data) => api.post('/provider-invoices/bulk-generate', data),
  getPendingCollections: (params) => api.get('/provider-invoices/pending-collections', { params }),
  getMissingCollections: (params) => api.get('/provider-invoices/missing-collections', { params }),
  update: (id, data) => api.put(`/provider-invoices/${id}`, data),
  delete: (id) => api.delete(`/provider-invoices/${id}`),
  addPayment: (id, data) => api.post(`/provider-invoices/${id}/payment`, data),
  toggleSent: (id) => api.patch(`/provider-invoices/${id}/toggle-sent`),
  downloadPDF: (id) => api.get(`/provider-invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Contractor Invoices
export const contractorInvoicesAPI = {
  getAll: (params) => api.get('/contractor-invoices', { params }),
  getById: (id) => api.get(`/contractor-invoices/${id}`),
  generate: (data) => api.post('/contractor-invoices/generate', data),
  preview: (data) => api.post('/contractor-invoices/preview', data),
  bulkGenerate: (data) => api.post('/contractor-invoices/bulk-generate', data),
  getPendingCollections: (params) => api.get('/contractor-invoices/pending-collections', { params }),
  update: (id, data) => api.put(`/contractor-invoices/${id}`, data),
  delete: (id) => api.delete(`/contractor-invoices/${id}`),
  addPayment: (id, data) => api.post(`/contractor-invoices/${id}/payment`, data),
  toggleSent: (id) => api.patch(`/contractor-invoices/${id}/toggle-sent`),
  downloadPDF: (id) => api.get(`/contractor-invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Tasks
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Documents
export const documentsAPI = {
  getAll: (params) => api.get('/documents', { params }),
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Reports
export const reportsAPI = {
  dashboard: () => api.get('/reports/dashboard'),
  collections: (params) => api.get('/reports/collections', { params }),
  providerBilling: (params) => api.get('/reports/provider-billing', { params }),
  contractorBilling: (params) => api.get('/reports/contractor-billing', { params }),
  profitability: (params) => api.get('/reports/profitability', { params }),
};

// Credentials
export const credentialsAPI = {
  getAll: (params) => api.get('/credentials', { params }),
  getById: (id) => api.get(`/credentials/${id}`),
  getStats: () => api.get('/credentials/stats'),
  create: (data) => api.post('/credentials', data),
  update: (id, data) => api.put(`/credentials/${id}`, data),
  delete: (id) => api.delete(`/credentials/${id}`),
  addActivity: (id, data) => api.post(`/credentials/${id}/activity`, data),
  uploadDocument: (id, formData) => api.post(`/credentials/${id}/upload-document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removeDocument: (id, docId) => api.delete(`/credentials/${id}/document/${docId}`),
  addPayerEnrollment: (id, data) => api.post(`/credentials/${id}/payer-enrollment`, data),
  updatePayerEnrollment: (id, enrollmentId, data) => api.put(`/credentials/${id}/payer-enrollment/${enrollmentId}`, data),
  addFollowUp: (id, data) => api.post(`/credentials/${id}/follow-up`, data),
  getExpiringAlerts: () => api.get('/credentials/expiring/alerts'),
  // Provider documents
  getProviderDocuments: (providerId) => api.get(`/credentials/provider/${providerId}/documents`),
  uploadProviderDocument: (providerId, formData) => api.post(`/credentials/provider/${providerId}/upload-document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // CAQH
  caqhScan: (providerId, formData) => api.post(`/credentials/provider/${providerId}/caqh-scan`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  caqhApply: (providerId, data) => api.post(`/credentials/provider/${providerId}/caqh-apply`, data),
  caqhManual: (providerId, data) => api.post(`/credentials/provider/${providerId}/caqh-manual`, data),
};

// Payments
export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getSummary: () => api.get('/payments/summary'),
  create: (data) => api.post('/payments', data),
  delete: (id) => api.delete(`/payments/${id}`),
};

// Schedules
export const schedulesAPI = {
  getAll: (params) => api.get('/schedules', { params }),
  create: (data) => api.post('/schedules', data),
  update: (id, data) => api.put(`/schedules/${id}`, data),
  delete: (id) => api.delete(`/schedules/${id}`),
  run: (id) => api.post(`/schedules/${id}/run`),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  updateEmail: (data) => api.put('/settings/email', data),
  updateSMS: (data) => api.put('/settings/sms', data),
};

// Profile
export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
  changePassword: (data) => api.put('/profile/change-password', data),
  getSecurity: () => api.get('/profile/security'),
  mfaSetup: () => api.post('/profile/mfa/setup'),
  mfaVerify: (data) => api.post('/profile/mfa/verify', data),
  mfaDisable: (data) => api.post('/profile/mfa/disable', data),
  clearSessions: () => api.delete('/profile/sessions'),
};

// Two-Factor Authentication
export const twofaAPI = {
  getStatus: () => api.get('/2fa/status'),
  setupTotp: () => api.post('/2fa/totp/setup'),
  confirmTotp: (data) => api.post('/2fa/totp/confirm', data),
  disableTotp: () => api.delete('/2fa/totp'),
  adminDisableTotp: (userId) => api.delete(`/2fa/totp/${userId}`),
  setPreferred: (data) => api.put('/2fa/preferred', data),
};

// Security (Admin)
export const securityAPI = {
  getSettings: () => api.get('/security/settings'),
  updateSettings: (data) => api.put('/security/settings', data),
  getUsersWithout2FA: () => api.get('/security/users-without-2fa'),
  getAuditLog: (params) => api.get('/security/audit-log', { params }),
  unlockUser: (userId) => api.post(`/security/unlock-user/${userId}`),
  resetUserPassword: (userId) => api.post(`/security/reset-password/${userId}`),
  toggleUser2FA: (userId, data) => api.put(`/security/user-2fa/${userId}`, data),
};

// Roles
export const rolesAPI = {
  getAll: () => api.get('/roles'),
  getAllIncludeInactive: () => api.get('/roles/all'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  seedDefaults: () => api.post('/roles/seed-defaults'),
  applyToUser: (roleId, userId) => api.post(`/roles/${roleId}/apply`, { userId }),
};

// Reminders
export const remindersAPI = {
  getAll: (params) => api.get('/reminders', { params }),
  getDashboard: () => api.get('/reminders/dashboard'),
  create: (data) => api.post('/reminders', data),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  complete: (id) => api.patch(`/reminders/${id}/complete`),
  dismiss: (id) => api.patch(`/reminders/${id}/dismiss`),
  delete: (id) => api.delete(`/reminders/${id}`),
};

export default api;
